//-- copyright
// OpenProject is an open source project management software.
// Copyright (C) the OpenProject GmbH
//
// This program is free software; you can redistribute it and/or
// modify it under the terms of the GNU General Public License version 3.
//
// OpenProject is a fork of ChiliProject, which is a fork of Redmine. The copyright follows:
// Copyright (C) 2006-2013 Jean-Philippe Lang
// Copyright (C) 2010-2013 the ChiliProject Team
//
// This program is free software; you can redistribute it and/or
// modify it under the terms of the GNU General Public License
// as published by the Free Software Foundation; either version 2
// of the License, or (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program; if not, write to the Free Software
// Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
//
// See COPYRIGHT and LICENSE files for more details.
//++

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  WorkPackageViewFiltersService,
} from 'core-app/features/work-packages/routing/wp-view-base/view-services/wp-view-filters.service';
import { DebouncedEventEmitter } from 'core-app/shared/helpers/rxjs/debounced-event-emitter';
import { QueryFilterInstanceResource } from 'core-app/features/hal/resources/query-filter-instance-resource';
import { from, fromEvent, merge, Observable } from 'rxjs';
import { UntilDestroyedMixin } from 'core-app/shared/helpers/angular/until-destroyed.mixin';
import { componentDestroyed } from '@w11k/ngx-componentdestroyed';
import {
  WorkPackageFiltersService,
} from 'core-app/features/work-packages/components/filters/wp-filters/wp-filters.service';
import { WorkPackagesListService } from 'core-app/features/work-packages/components/wp-list/wp-list.service';

// Only where the panel actually floats. On the work package page it is hung
// under the toolbar as a dropdown (content/_gpe_filters.sass) and a click on
// the page behind it has to put it away; the copies that open in the flow of
// the page - an embedded table's, the one on the configuration modal's filter
// tab - overlap nothing, and closing those on a click elsewhere would take the
// panel away from under someone still reading the list it filters.
const FLOATING_PANEL_HOST_SELECTOR = '.work-packages-partitioned-query-space--filter-area';

// The button lives in the toolbar, outside this component, and toggles the
// panel itself - closing here on the way down would let it reopen on the way
// back up.
const FILTER_TOGGLE_SELECTOR = '#work-packages-filter-toggle-button';

// Controls inside the panel that open in a layer attached to the body rather
// than inside the panel's own box, so that the panel's scroll does not clip
// them. A click landing in one of those is a click inside the panel.
const PANEL_OVERLAY_SELECTOR = [
  '.ng-dropdown-panel',
  '.flatpickr-calendar',
  '.spot-drop-modal--body',
  'dialog',
].join(', ');

@Component({
  templateUrl: './filter-container.directive.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'op-filter-container',
})
export class WorkPackageFilterContainerComponent extends UntilDestroyedMixin implements OnInit, OnDestroy {
  @Input('showFilterButton') showFilterButton = false;

  @Input('filterButtonText') filterButtonText:string = I18n.t('js.button_filter');

  @Output() public filtersChanged = new DebouncedEventEmitter<QueryFilterInstanceResource[]>(componentDestroyed(this));

  public visible$:Observable<boolean>;

  public filters = this.wpTableFilters.current;

  public loaded = false;

  constructor(
    readonly wpTableFilters:WorkPackageViewFiltersService,
    readonly cdRef:ChangeDetectorRef,
    readonly wpFiltersService:WorkPackageFiltersService,
    readonly wpListService:WorkPackagesListService,
    readonly elementRef:ElementRef<HTMLElement>,
    readonly ngZone:NgZone,
  ) {
    super();
    this.visible$ = this.wpFiltersService.observeUntil(componentDestroyed(this));
  }

  ngOnInit():void {
    merge(
      this.wpTableFilters.pristine$(),
      from(this.wpListService.conditionallyLoadForm()),
    )
      .pipe(
        this.untilDestroyed(),
      )
      .subscribe(() => {
        this.filters = this.wpTableFilters.current;
        this.loaded = true;
        this.cdRef.detectChanges();
      });

    this.watchForDismissal();
  }

  // Outside the zone: these fire on every click and every key anywhere in the
  // document, and this page carries a table that does not need a change
  // detection pass for each of them. The one case that changes anything steps
  // back in on its own.
  private watchForDismissal():void {
    this.ngZone.runOutsideAngular(() => {
      fromEvent<MouseEvent>(document, 'click', { capture: true })
        .pipe(this.untilDestroyed())
        .subscribe((event) => this.closeOnOutsideClick(event));

      fromEvent<KeyboardEvent>(document, 'keydown')
        .pipe(this.untilDestroyed())
        .subscribe((event) => this.closeOnEscape(event));
    });
  }

  private get floating():boolean {
    return !!this.elementRef.nativeElement.closest(FLOATING_PANEL_HOST_SELECTOR);
  }

  private closeOnOutsideClick(event:MouseEvent):void {
    if (!this.floating || !this.wpFiltersService.visible) { return; }

    const { target } = event;
    if (!(target instanceof Element)) { return; }

    if (this.elementRef.nativeElement.contains(target)) { return; }
    if (target.closest(FILTER_TOGGLE_SELECTOR)) { return; }
    if (target.closest(PANEL_OVERLAY_SELECTOR)) { return; }

    this.close();
  }

  private closeOnEscape(event:KeyboardEvent):void {
    if (!this.floating || !this.wpFiltersService.visible) { return; }
    if (event.key !== 'Escape') { return; }

    // Whatever is open inside the panel answers the key first - an
    // autocompleter's list, a datepicker - and the panel only closes once
    // nothing in it claimed it.
    if (event.defaultPrevented) { return; }

    this.close();

    const toggle = document.querySelector<HTMLElement>(FILTER_TOGGLE_SELECTOR);
    toggle?.focus();
  }

  private close():void {
    this.ngZone.run(() => {
      this.wpFiltersService.visible = false;
    });
  }

  public replaceIfComplete(filters:QueryFilterInstanceResource[]) {
    const available = filters.filter((el) => this.wpTableFilters.isAvailable(el));
    this.wpTableFilters.replaceIfComplete(available);
    this.filtersChanged.emit(available);
  }

  // Reset drops the whole working set and reloads the view's default query -
  // the server-defined filters (status open on the stock default), not just a
  // cleared list - which is the one already used when the module is opened with
  // no query. The panel is put away afterwards, as it is on any other reload.
  public resetToDefault() {
    this.wpListService.resetToDefaultQuery();
    this.close();
  }
}
